import { ApiResponse } from "@/utils/api/ApiProps";
import { TeamsApiData, TeamDetail, TeamHierarchy, CreateTeamRequest, UpdateTeamRequest } from "@/types/settings/TeamTypes";
import api from "@/utils/api";

interface GetTeamsParams {
  page?: number;
  search_term?: string;
  sort_column?: string;
  sort_order?: "ASC" | "DESC";
  department?: string;
  includeMembers?: boolean;
  limit?: number;
}

// Get all teams with pagination
export const getTeams = async ({
  page = 1,
  search_term = "",
  sort_column,
  sort_order,
  department,
  includeMembers = false,
  limit = 30
}: GetTeamsParams = {}): Promise<ApiResponse<TeamsApiData>> => {
  const searchParams = new URLSearchParams();

  searchParams.append("page", page.toString());
  if (search_term) searchParams.append("search_term", search_term);
  if (sort_column) searchParams.append("sort_column", sort_column);
  if (sort_order) searchParams.append("sort_order", sort_order);
  if (department) searchParams.append("department", department);
  if (includeMembers) searchParams.append("includeMembers", "true");
  if (limit) searchParams.append("limit", limit.toString());

  const queryString = searchParams.toString();
  const url = queryString ? `/teams?${queryString}` : "/teams";

  return api.get<TeamsApiData>(url);
};

// Get team by UUID
export const getTeamById = async (
  uuid: string
): Promise<ApiResponse<{ data: TeamDetail }>> => {
  const response = await api.req<{ data: TeamDetail }>({
    url: `/teams/${uuid}`,
    method: api.Method.GET,
  });
  return response;
};

// Get team hierarchy
export const getTeamHierarchy = async (): Promise<ApiResponse<TeamHierarchy[]>> => {
  const response = await api.req<TeamHierarchy[]>({
    url: "/teams/hierarchy",
    method: api.Method.GET,
  });
  return response;
};

// Create team
export const createTeam = async (
  data: CreateTeamRequest
): Promise<ApiResponse<{ data: TeamDetail }>> => {
  const response = await api.req<{ data: TeamDetail }>({
    url: "/teams",
    method: api.Method.POST,
    body: data,
  });
  return response;
};

// Update team
export const updateTeam = async (
  uuid: string,
  data: UpdateTeamRequest
): Promise<ApiResponse<{ data: TeamDetail }>> => {
  const response = await api.req<{ data: TeamDetail }>({
    url: `/teams/${uuid}`,
    method: api.Method.PUT,
    body: data,
  });
  return response;
};

// Delete team
export const deleteTeam = async (
  uuid: string
): Promise<ApiResponse<{ data: any }>> => {
  try {
    const response = await api.req<{ data: any }>({
      url: `/teams/${uuid}`,
      method: api.Method.DELETE,
    });

    if (response.status !== "success") {
      throw new Error(response.message || "Failed to delete team");
    }

    return response;
  } catch (error) {
    console.error(`Error deleting team ${uuid}:`, error);
    throw new Error("Failed to delete team");
  }
};

// Get team members
export const getTeamMembers = async (
  teamUuid: string
): Promise<ApiResponse<{
  team: TeamDetail;
  team_lead: any;
  members: any[];
  member_count: number;
}>> => {
  const response = await api.req<{
    team: TeamDetail;
    team_lead: any;
    members: any[];
    member_count: number;
  }>({
    url: `/teams/${teamUuid}/members`,
    method: api.Method.GET,
  });
  return response;
};

// Add members to team
export const addTeamMembers = async (
  teamUuid: string,
  userUuids: string[]
): Promise<ApiResponse<{
  team_uuid: string;
  team_name: string;
  added_members: any[];
  errors?: string[];
}>> => {
  const response = await api.req<{
    team_uuid: string;
    team_name: string;
    added_members: any[];
    errors?: string[];
  }>({
    url: `/teams/${teamUuid}/members`,
    method: api.Method.POST,
    body: { user_uuids: userUuids },
  });
  return response;
};

// Remove members from team
export const removeTeamMembers = async (
  teamUuid: string,
  userUuids: string[]
): Promise<ApiResponse<{
  team_uuid: string;
  team_name: string;
  removed_members: any[];
  errors?: string[];
}>> => {
  const response = await api.req<{
    team_uuid: string;
    team_name: string;
    removed_members: any[];
    errors?: string[];
  }>({
    url: `/teams/${teamUuid}/members`,
    method: api.Method.DELETE,
    body: { user_uuids: userUuids },
  });
  return response;
};